import { useEffect, useState } from "react";
import { Upload, FileCheck, AlertCircle, Loader2, ScrollText, History } from "lucide-react";
import api from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const SAMPLE = `title,price,city,neighborhood,bedrooms,bathrooms,sqft,transaction_type,property_type
Sunset Bay Condo,540000,Miami,Edgewater,2,2,1100,sale,condo
Palm Grove Cottage,780000,Miami,Coconut Grove,3,2,1800,sale,house
Downtown Studio Rental,2800,Miami,Downtown,1,1,600,rent,apartment`;

function parseCSV(text) {
  const lines = text.trim().split("\n");
  const headers = lines[0].split(",").map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const vals = line.split(",");
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (vals[i] || "").trim(); });
    return obj;
  });
}

export function ImportAudit() {
  const { t } = useLang();
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState([]);
  const [history, setHistory] = useState([]);

  const loadLogs = () => {
    api.get("/admin/audit").then((r) => setAudit(r.data.items));
    api.get("/admin/import/history").then((r) => setHistory(r.data.items));
  };
  useEffect(() => { loadLogs(); }, []);

  const doPreview = async () => {
    const parsed = parseCSV(csv);
    setRows(parsed);
    const { data } = await api.post("/admin/import/preview", { rows: parsed });
    setPreview(data);
  };

  const doCommit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/import/commit", { rows });
      toast.success(`Imported ${data.imported} listings`);
      setCsv(""); setPreview(null); setRows([]); loadLogs();
    } catch { toast.error("Import failed"); } finally { setLoading(false); }
  };

  return (
    <div>
      <h1 className="font-serif text-4xl font-light mb-8">{t("dash.import")}</h1>
      <Tabs defaultValue="import">
        <TabsList className="mb-6">
          <TabsTrigger value="import" data-testid="tab-import"><Upload size={14} className="mr-2" /> Import</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History size={14} className="mr-2" /> History</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText size={14} className="mr-2" /> {t("dash.audit")}</TabsTrigger>
        </TabsList>

        <TabsContent value="import">
          <div className="bg-card border border-border p-6">
            <div className="flex items-center justify-between mb-3">
              <label className="label-caps text-muted-foreground">Paste CSV data</label>
              <button onClick={() => setCsv(SAMPLE)} className="text-xs text-primary hover:underline" data-testid="load-sample">Load sample</button>
            </div>
            <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={8} placeholder="title,price,city,bedrooms…" className="w-full bg-background border border-border focus:border-primary px-3 py-3 text-sm outline-none font-mono resize-none" data-testid="csv-input" />
            <div className="flex gap-3 mt-4">
              <button onClick={doPreview} disabled={!csv} className="px-5 py-2.5 border border-primary text-primary label-caps disabled:opacity-40 hover:bg-primary hover:text-primary-foreground transition-colors" data-testid="preview-btn">Preview</button>
              {preview && preview.valid_count > 0 && (
                <button onClick={doCommit} disabled={loading} className="px-5 py-2.5 bg-primary text-primary-foreground label-caps disabled:opacity-50 flex items-center gap-2" data-testid="commit-btn">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <FileCheck size={14} />} Import {preview.valid_count}
                </button>
              )}
            </div>

            {preview && (
              <div className="mt-6" data-testid="import-preview">
                <div className="flex gap-4 mb-4">
                  <span className="flex items-center gap-2 text-emerald-400 text-sm"><FileCheck size={15} /> {preview.valid_count} valid</span>
                  {preview.error_count > 0 && <span className="flex items-center gap-2 text-red-400 text-sm"><AlertCircle size={15} /> {preview.error_count} errors</span>}
                </div>
                {preview.errors?.length > 0 && (
                  <div className="mb-4 text-sm text-red-400 space-y-1">
                    {preview.errors.map((e, i) => <p key={i}>Row {e.row}: {e.error}</p>)}
                  </div>
                )}
                <div className="border border-border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border label-caps text-[0.6rem] text-muted-foreground text-left">
                      <th className="p-3">Title</th><th className="p-3">Price</th><th className="p-3">City</th><th className="p-3">Beds</th>
                    </tr></thead>
                    <tbody>
                      {preview.preview?.map((r, i) => (
                        <tr key={i} className="border-b border-border last:border-0"><td className="p-3">{r.title}</td><td className="p-3">{r.price}</td><td className="p-3">{r.city}</td><td className="p-3">{r.bedrooms}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="bg-card border border-border divide-y divide-border" data-testid="import-history">
            {history.length === 0 ? <p className="p-6 text-muted-foreground font-light">No imports yet.</p> :
              history.map((h) => (
                <div key={h.id} className="p-4 flex justify-between text-sm">
                  <span>Imported <span className="text-primary">{h.count}</span> listings by {h.by}</span>
                  <span className="text-muted-foreground">{new Date(h.at).toLocaleString()}</span>
                </div>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="bg-card border border-border divide-y divide-border" data-testid="audit-log">
            {audit.length === 0 ? <p className="p-6 text-muted-foreground font-light">No activity yet.</p> :
              audit.map((a) => (
                <div key={a.id} className="p-4 flex justify-between text-sm">
                  <span><span className="text-primary capitalize">{a.action}</span> {a.entity} — {a.label} <span className="text-muted-foreground">by {a.user}</span></span>
                  <span className="text-muted-foreground">{new Date(a.at).toLocaleString()}</span>
                </div>
              ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
